import { useState, useMemo } from "react";
import { useExchangeRates } from "@/hooks/useExchangeRates";
import { CurrencyToggle } from "@/components/currency-toggle/CurrencyToggle";
import { TotalAmountCard } from "./TotalAmountCard";
import { DebtIssuerDistributionChart } from "./DebtIssuerDistributionChart";
import { computeTotalAmount } from "./utils";
import { DebtSecuritiesOverviewProps, DebtSecurityWithConvertedValue } from "./types";

/**
 * Overview section for the Debt Securities page.
 * Renders a currency toggle, total amount card, and issuer distribution chart.
 * Requirements: 1.1, 2.3, 2.4, 2.6, 2.7, 4.1–4.7, 5.1, 5.3–5.6
 */
export function DebtSecuritiesOverview({ debtSecurities, isLoading: securitiesLoading }: DebtSecuritiesOverviewProps) {
  const defaultCurrency = debtSecurities[0]?.currency ?? "USD";
  const [displayCurrency, setDisplayCurrency] = useState(defaultCurrency);

  const { convert, isLoading: ratesLoading, error: ratesError } = useExchangeRates(defaultCurrency);

  const isLoading = securitiesLoading || ratesLoading;
  const hasError = !!ratesError;

  // Compute converted securities array.
  const convertedSecurities = useMemo<DebtSecurityWithConvertedValue[]>(() => {
    if (hasError || ratesLoading) return [];
    return debtSecurities.map((s) => {
      let convertedFaceValue = s.face_value;
      try {
        convertedFaceValue = convert(s.face_value, s.currency, displayCurrency);
      } catch {
        // Fall back to raw face_value if conversion fails.
      }
      return { ...s, convertedFaceValue };
    });
  }, [debtSecurities, displayCurrency, convert, hasError, ratesLoading]);

  const total = useMemo(() => computeTotalAmount(convertedSecurities), [convertedSecurities]);

  // Build unconverted summary for error state: "USD 10,000.00 + EUR 5,000.00"
  const unconvertedSummary = useMemo(() => {
    if (!hasError) return undefined;
    const byCurrency = new Map<string, number>();
    for (const s of debtSecurities) {
      byCurrency.set(s.currency, (byCurrency.get(s.currency) ?? 0) + s.face_value);
    }
    return Array.from(byCurrency.entries())
      .map(([cur, amt]) =>
        `${cur} ${new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amt)}`
      )
      .join(" + ");
  }, [debtSecurities, hasError]);

  return (
    <section
      aria-label="Debt securities portfolio overview"
      aria-busy={isLoading}
    >
      {hasError && (
        <div role="alert" className="sr-only">
          Currency conversion is unavailable. Showing unconverted totals.
        </div>
      )}

      <div className="mb-4">
        <CurrencyToggle
          baseCurrency={defaultCurrency}
          selectedCurrency={displayCurrency}
          onCurrencyChange={setDisplayCurrency}
          disabled={ratesLoading}
          showBalance={false}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TotalAmountCard
          total={total}
          currency={displayCurrency}
          isLoading={isLoading}
          hasError={hasError}
          unconvertedSummary={unconvertedSummary}
        />
        <DebtIssuerDistributionChart
          debtSecurities={convertedSecurities}
          currency={displayCurrency}
          isLoading={isLoading}
        />
      </div>
    </section>
  );
}
